// What needs an operator's action right now — the map's Attention tab and its
// live alerts. Leon's call (admin overhaul intake, W5): "strip on the
// dashboard, full attention on the map".
//
// Every rule states a FACT ("25 min past schedule, not started"), never a
// remedy — who may refund, reassign or cancel is an Open Item
// (never-invent-policy.md).
//
// ⚠ OPEN: the two dashboards disagree on "overdue" — the cross-hub view flags
// a pre-session booking 30 minutes past schedule, the hub view at any lateness.
// The map uses 30 because it is the threshold on Bam's own dashboard; it is one
// constant so the decision is a one-line change when it is made.
//
// ID submissions (Leon, 2026-10-03) are ONE line, a count — never a client's
// name: the strip and the map are operations surfaces, and the KYC page is
// where a submission is opened (and audited). Superadmin only, because only
// superadmin holds a grant on the KYC tables (0070); other roles pass nothing.
//
// ⚠ NOT HERE: SOS. It exists now (0273, features/sos) and is deliberately kept
// out of this list: it is pinned on the map and in its own lane above the
// alert stack, exempt from the three-alert limit (BL-18/BL-19,
// live-map.alerts.ts).

import type { StaffStatusView } from "./live-map.status";

export const OVERDUE_AFTER_MINUTES = 30;

/**
 * BL-21 — a therapist waiting this long for her return ride is an incident.
 *
 * ⚠ IT RAISES AN ALERT AND NOTHING ELSE. The backlog line reads "becomes
 * priority in the rider dispatch queue", but what that means mechanically is
 * undefined: CORE-02 §4.5 puts Therapist Return in the LOWEST priority class
 * "according to current operational queue rules", and those rules are not
 * written down. Whether an hour-old wait should jump ASAP client dispatches, or
 * only re-sort within its own class, is Luigi's to decide — so dispatch
 * ordering is untouched here and the operator is told instead (Leon,
 * 2026-10-04). One constant, so the threshold is a one-line change.
 */
export const RETURN_WAIT_ALERT_MINUTES = 60;

const PRE_SESSION = new Set([
  "pending",
  "awaiting_therapist",
  "awaiting_rider",
  "dispatched",
  "en_route_to_client",
  "arrived",
]);

export type AttentionTarget =
  | { kind: "booking"; bookingId: string; ref: string }
  | { kind: "staff"; staffId: string }
  | { kind: "page"; href: string };

export type AttentionKind =
  | "payment"
  | "no_show"
  | "disconnected"
  | "late"
  | "no_rider"
  | "cash"
  | "kyc"
  | "return_wait";

export interface LiveAttentionItem {
  /** Stable across polls — the alert stack uses it to tell new from seen. */
  id: string;
  kind: AttentionKind;
  tone: "danger" | "warning";
  /** One short line naming the problem and whom it concerns. */
  title: string;
  /** The supporting fact, never a remedy. */
  detail: string;
  at: string | null;
  hubId: string | null;
  target: AttentionTarget;
}

/** The only booking fields the rules read — so the dashboard's command feed
 * and the map's own read both fit without a second bookings query. */
export type AttentionBooking = {
  id: string;
  ref: string;
  status: string;
  scheduled_at: string;
  hub_id: string;
};

export interface AttentionNoShow {
  id: string;
  party: string;
  created_at: string | null;
  booking_id: string;
  bookings: { ref: string; hub_id: string } | null;
}

export interface AttentionInput {
  nowMs: number;
  hubNames: ReadonlyMap<string, string>;
  bookings: readonly AttentionBooking[];
  staff: readonly {
    id: string;
    name: string;
    hubId: string;
    status: StaffStatusView;
  }[];
  noShows: readonly AttentionNoShow[];
  paymentExceptions: readonly {
    id: string;
    kind: string;
    booking_id: string | null;
    created_at: string;
    text: string;
    ref: string | null;
  }[];
  pendingCash: readonly { id: string; amount: number; hub_id: string }[];
  /** Therapists waiting to be collected for their return trip (BL-21). */
  returnWaits?: readonly {
    id: string;
    therapistName: string;
    hubId: string | null;
    /** When the return dispatch was created — service done, ride owed. */
    waitingSince: string;
    riderName: string | null;
  }[];
  /** IDs submitted and not yet decided. Omit for roles without the KYC grant. */
  kycAwaiting?: { count: number; oldestAt: string | null } | null;
}

/** A report with no booking has nothing to point at, so it cannot be an item. */
export function toNoShowAttentionInputs(
  rows: readonly (Omit<AttentionNoShow, "booking_id"> & { booking_id: string | null })[],
): AttentionNoShow[] {
  return rows.flatMap((report) =>
    report.booking_id ? [{ ...report, booking_id: report.booking_id }] : [],
  );
}

/** Items keyed by the booking they concern — the tables' Attention column. */
export function attentionByBookingId(
  items: readonly LiveAttentionItem[],
): Map<string, LiveAttentionItem[]> {
  const byId = new Map<string, LiveAttentionItem[]>();
  for (const item of items) {
    if (item.target.kind !== "booking") continue;
    byId.set(item.target.bookingId, [...(byId.get(item.target.bookingId) ?? []), item]);
  }
  return byId;
}

function minutesPast(iso: string, nowMs: number): number {
  return Math.floor((nowMs - Date.parse(iso)) / 60_000);
}

function peso(amount: number): string {
  return `₱${amount.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function buildLiveAttention(input: AttentionInput): LiveAttentionItem[] {
  const items: LiveAttentionItem[] = [];

  // Money that already moved with nothing following it (0093) leads.
  for (const row of input.paymentExceptions) {
    items.push({
      id: `payment-${row.id}`,
      kind: "payment",
      tone: "danger",
      title: row.ref ? `Payment issue · ${row.ref}` : "Payment issue",
      detail: row.text,
      at: row.created_at,
      hubId: null,
      target: row.booking_id
        ? { kind: "booking", bookingId: row.booking_id, ref: row.ref ?? "" }
        : { kind: "page", href: "/bookings" },
    });
  }

  for (const report of input.noShows) {
    const ref = report.bookings?.ref ?? "Booking";
    items.push({
      id: `no-show-${report.id}`,
      kind: "no_show",
      tone: "danger",
      title: `No-show · ${ref}`,
      detail: `The ${report.party} was reported as a no-show`,
      at: report.created_at,
      hubId: report.bookings?.hub_id ?? null,
      target: { kind: "booking", bookingId: report.booking_id, ref: report.bookings?.ref ?? "" },
    });
  }

  // A staffer mid-job whose phone went quiet — the case the status sheet exists
  // for. Idle staff going quiet is not an incident; working ones are.
  for (const member of input.staff) {
    if (member.status.group !== "disconnected" || !member.status.fromEngine) continue;
    if (member.status.activity === "active") continue;
    items.push({
      id: `disconnected-${member.id}`,
      kind: "disconnected",
      tone: "danger",
      title: `${member.name} lost connection`,
      detail: member.status.label,
      at: member.status.since,
      hubId: member.hubId,
      target: { kind: "staff", staffId: member.id },
    });
  }

  for (const booking of input.bookings) {
    const late = minutesPast(booking.scheduled_at, input.nowMs);
    if (PRE_SESSION.has(booking.status) && late > OVERDUE_AFTER_MINUTES) {
      items.push({
        id: `overdue-${booking.id}`,
        kind: "late",
        tone: "danger",
        title: `${booking.ref} is running late`,
        detail: `Not started, ${late} min past schedule`,
        at: booking.scheduled_at,
        hubId: booking.hub_id,
        target: { kind: "booking", bookingId: booking.id, ref: booking.ref },
      });
    } else if (booking.status === "awaiting_rider") {
      items.push({
        id: `rider-${booking.id}`,
        kind: "no_rider",
        tone: "warning",
        title: `${booking.ref} has no rider`,
        detail: "Waiting for a rider to be assigned",
        at: booking.scheduled_at,
        hubId: booking.hub_id,
        target: { kind: "booking", bookingId: booking.id, ref: booking.ref },
      });
    }
  }

  // One line per hub, not per row: the action is "go receive it", once.
  const cashByHub = new Map<string, { count: number; total: number }>();
  for (const row of input.pendingCash) {
    const entry = cashByHub.get(row.hub_id) ?? { count: 0, total: 0 };
    cashByHub.set(row.hub_id, { count: entry.count + 1, total: entry.total + row.amount });
  }
  for (const [hubId, { count, total }] of cashByHub) {
    items.push({
      id: `cash-${hubId}-${count}`,
      kind: "cash",
      tone: "warning",
      title: `Cash waiting at ${input.hubNames.get(hubId) ?? "a hub"}`,
      detail: `${count} remittance${count === 1 ? "" : "s"} · ${peso(total)}`,
      at: null,
      hubId,
      target: { kind: "page", href: "/cash" },
    });
  }

  // BL-21 — service finished, still nobody has collected her.
  for (const wait of input.returnWaits ?? []) {
    const minutes = minutesPast(wait.waitingSince, input.nowMs);
    if (minutes < RETURN_WAIT_ALERT_MINUTES) continue;
    const hours = Math.floor(minutes / 60);
    const waited = hours >= 1 ? `${hours} hr ${minutes % 60} min` : `${minutes} min`;
    items.push({
      id: `return-wait-${wait.id}`,
      kind: "return_wait",
      tone: "danger",
      title: `${wait.therapistName} is waiting to go home`,
      // The fact, never the remedy: who re-prioritises a rider is undefined.
      detail: wait.riderName
        ? `${waited} since her service ended · ${wait.riderName} assigned, not collected`
        : `${waited} since her service ended · no rider assigned`,
      at: wait.waitingSince,
      hubId: wait.hubId,
      target: { kind: "page", href: "/dispatch" },
    });
  }

  const kyc = input.kycAwaiting;
  if (kyc && kyc.count > 0) {
    items.push({
      // The count is in the id so a new submission reads as a new alert.
      id: `kyc-${kyc.count}`,
      kind: "kyc",
      tone: "warning",
      title: `${kyc.count} ID${kyc.count === 1 ? "" : "s"} waiting for review`,
      detail: "Submitted for verification, not yet decided",
      at: kyc.oldestAt,
      hubId: null,
      target: { kind: "page", href: "/kyc" },
    });
  }

  return items.sort((a, b) => (a.tone === b.tone ? 0 : a.tone === "danger" ? -1 : 1));
}
